import { create } from 'zustand'
import type { Captador, MatriculaListItem } from '@/types'
import * as parceirosApi from '@/api/parceiros'
import { useAuthStore } from '@/stores/authStore'
import type { AlunosFilters } from '@/api/parceiros'
import { toast } from 'react-toastify'

interface AlunosState {
  data: MatriculaListItem[]
  totalRecords: number
  loading: boolean
  error: string | null
  filters: AlunosFilters
  selectedIds: number[]
  cobrancaAgregada: { id: number; pix: string } | null
  cobrancaAgregadaLoading: boolean
  setFilter: (key: keyof AlunosFilters, value: AlunosFilters[keyof AlunosFilters]) => void
  resetFilters: () => void
  fetchAlunos: () => Promise<void>
  setSelectedIds: (ids: number[]) => void
  clearSelection: () => void
  setCobrancaAgregada: (cobranca: { id: number; pix: string } | null) => void
  gerarCobrancaAgregada: () => Promise<void>
  captadores: Captador[]
  buscandoPorCpf: boolean
  matriculasDoAluno: parceirosApi.MatriculaDoAluno[]
  carregarCaptadores: () => Promise<void>
  buscarMatriculasPorCpf: (cpf: string) => Promise<parceirosApi.MatriculaDoAluno[]>
  limparMatriculasDoAluno: () => void
}

const defaultFilters: AlunosFilters = {
  limit: 10,
  pageNumber: 1,
}

export const useAlunosStore = create<AlunosState>((set, get) => ({
  data: [],
  totalRecords: 0,
  loading: false,
  error: null,
  filters: defaultFilters,
  selectedIds: [],
  cobrancaAgregada: null,
  cobrancaAgregadaLoading: false,
  captadores: [],
  buscandoPorCpf: false,
  matriculasDoAluno: [],

  setFilter: (key, value) => {
    set({ filters: { ...get().filters, [key]: value } })
  },

  resetFilters: () => {
    set({ filters: defaultFilters, selectedIds: [] })
  },

  setSelectedIds: (ids) => set({ selectedIds: ids }),

  clearSelection: () => set({ selectedIds: [] }),

  setCobrancaAgregada: (cobranca) => set({ cobrancaAgregada: cobranca }),

  gerarCobrancaAgregada: async () => {
    const { selectedIds } = get()
    set({ cobrancaAgregadaLoading: true })
    try {
      const result = await parceirosApi.createCobrancaAgregada(selectedIds)
      set({ cobrancaAgregada: { id: result.id, pix: result.infoPix.pix } })
    } catch {
      toast.error('Erro ao gerar cobrança. Tente novamente.')
    } finally {
      set({ cobrancaAgregadaLoading: false })
    }
  },

  carregarCaptadores: async () => {
    try {
      set({ captadores: await parceirosApi.listarCaptadores() })
    } catch {
      set({ captadores: [] })
    }
  },

  buscarMatriculasPorCpf: async (cpf) => {
    set({ buscandoPorCpf: true, matriculasDoAluno: [] })
    try {
      const matriculas = await parceirosApi.buscarMatriculasPorCpf(cpf)
      if (matriculas.length > 1) set({ matriculasDoAluno: matriculas })
      return matriculas
    } catch {
      toast.error('Erro ao buscar o aluno. Tente novamente.')
      return []
    } finally {
      set({ buscandoPorCpf: false })
    }
  },

  limparMatriculasDoAluno: () => set({ matriculasDoAluno: [] }),

  fetchAlunos: async () => {
    const parceiro = useAuthStore.getState().parceiro
    if (!parceiro) return
    set({ loading: true, error: null })
    try {
      const result = await parceirosApi.getAlunos(get().filters)
      set({ data: result.data, totalRecords: result.totalRecords })
    } catch {
      set({ error: 'Erro ao carregar alunos. Verifique sua conexão e tente novamente.' })
    } finally {
      set({ loading: false })
    }
  },
}))
